type OnboardingScreen = 'onboarding-welcome' | 'onboarding-add-pc';

export interface OnboardingGateInputs {
  needsWelcome: boolean;
  needsWelcomeNoTier: boolean;
  needsAttestation: boolean;
  /**
   * An assigned `tier-0` machine always stays on the welcome screen.
   * Completing onboarding while the machine was a higher tier must not
   * open the hub after a later demotion.
   */
  stuckOnTierZero?: boolean;
}

export type OnboardingResult = OnboardingScreen | 'hub';

type Listener = (screen: OnboardingResult) => void;

const LOG_PREFIX = '[OnboardingFlow]';

/**
 * Evaluates onboarding gates, queues the applicable screens in order
 * (add-pc → tiers), and advances through them one at a time.
 * When the queue is exhausted the result is 'hub'.
 *
 * Order matters and is deliberate: Add PC comes BEFORE the tiers page. The
 * evaluation loading screens stay in front of both. Welcome-seen is written by
 * the tiers page's own continue, not by Add PC completing — so leaving Add PC
 * must never mark the user as welcomed.
 */
export class OnboardingFlowManager {
  private queue: OnboardingScreen[] = [];
  private currentIndex = 0;
  private evaluated = false;
  private holdOnWelcome = false;
  private listeners: Listener[] = [];

  evaluate(inputs: OnboardingGateInputs): void {
    if (this.evaluated) {
      console.warn(LOG_PREFIX, 'Already evaluated — ignoring duplicate call');
      return;
    }

    console.log(LOG_PREFIX, 'Evaluating onboarding gates:', {
      needsWelcome: inputs.needsWelcome,
      needsWelcomeNoTier: inputs.needsWelcomeNoTier,
      needsAttestation: inputs.needsAttestation,
    });

    this.queue = [];
    this.currentIndex = 0;
    this.holdOnWelcome = inputs.stuckOnTierZero === true;

    // Add PC first: the machine must be attested before the tiers page is shown.
    if (inputs.needsAttestation) {
      this.queue.push('onboarding-add-pc');
      console.log(LOG_PREFIX, '  ✓ Attestation gate PASSED — queued onboarding-add-pc');
    } else {
      console.log(LOG_PREFIX, '  ✗ Attestation gate SKIPPED — user is already attested');
    }

    if (inputs.stuckOnTierZero || inputs.needsWelcome || inputs.needsWelcomeNoTier) {
      this.queue.push('onboarding-welcome');
      const reason = inputs.stuckOnTierZero
        ? 'machine is tier-0'
        : inputs.needsWelcome
          ? 'user changed'
          : 'machine has no tier';
      console.log(LOG_PREFIX, `  ✓ Tiers gate PASSED (${reason}) — queued onboarding-welcome`);
    } else {
      console.log(LOG_PREFIX, '  ✗ Tiers gate SKIPPED — user has already seen welcome');
    }

    this.evaluated = true;
    const screen = this.getCurrentScreen();
    console.log(LOG_PREFIX, 'Initial screen:', screen, `(${this.queue.length} onboarding step(s) queued)`);
    this.notify();
  }

  advance(): void {
    if (!this.evaluated) {
      console.warn(LOG_PREFIX, 'advance() called before evaluate()');
      return;
    }
    const prev = this.getCurrentScreen();
    if (this.holdOnWelcome && prev === 'onboarding-welcome') {
      console.log(LOG_PREFIX, 'Tier 0 holds the welcome screen');
      return;
    }
    this.currentIndex++;
    const next = this.getCurrentScreen();
    console.log(LOG_PREFIX, `Advancing: ${prev} → ${next}`);
    this.notify();
  }

  /**
   * Clear the one-shot evaluation so the gates can be re-evaluated from
   * scratch. Used when the signed-in user changes mid-session: the stale
   * attestation status from the previous user must not pin the queue.
   * A fresh evaluate() must follow before getCurrentScreen() is meaningful.
   */
  reset(): void {
    this.queue = [];
    this.currentIndex = 0;
    this.evaluated = false;
    this.holdOnWelcome = false;
    console.log(LOG_PREFIX, 'Reset — gates will be re-evaluated on the next evaluate()');
  }

  getCurrentScreen(): OnboardingResult {
    if (!this.evaluated) return 'hub';
    if (this.currentIndex >= this.queue.length) return 'hub';
    return this.queue[this.currentIndex];
  }

  isEvaluated(): boolean {
    return this.evaluated;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.push(listener);
    return () => {
      const idx = this.listeners.indexOf(listener);
      if (idx > -1) this.listeners.splice(idx, 1);
    };
  }

  private notify(): void {
    const screen = this.getCurrentScreen();
    for (const listener of this.listeners) {
      listener(screen);
    }
  }
}
